import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import updateLocale from "dayjs/plugin/updateLocale"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { FlatList, Image, ListRenderItemInfo, StyleSheet, Text, View } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { useSetRecoilState } from "recoil"
import { Button } from "src/components/parts/buttons/Button"
import { ButtonType, IconButton } from "src/components/parts/buttons/IconButton"
import { BadgeIcon } from "src/components/parts/icons/BadgeIcon"
import { CustomRefreshControl } from "src/components/projects/CustomRefreshControl"
import { Colors } from "src/constants/Colors"
import { RootTabs } from "src/constants/RootTabs"
import { Screens } from "src/constants/Screens"
import { OrganizationManagerScreens } from "src/constants/OrganizationManagerScreens"
import { ContactNetworkDrawers } from "src/constants/ContactNetworkDrawers"
import { MainLayout } from "src/layouts/MainLayout"
import { RootTabScreenProps } from "src/types.d"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { notificationsSelector } from "src/recoil/selectors/home/notificationsSelector"
import { homeOrganizationsSelector } from "src/recoil/selectors/home/HomeOrganizationsSelector"
import { tokensState } from "src/recoil/atoms/authorization/tokensState"
import { selectedMyOrganizationState } from "src/recoil/atoms/organization/selectedMyOrganizationState"
import { defaultContactFilterState } from "src/recoil/atoms/contact/defaultContactFilterState"
import { notificationsRequestIdState } from "src/recoil/atoms/home/notificationsRequestIdState"
import { homeOrganizationsRequestIdState } from "src/recoil/atoms/home/homeOrganizationsRequestIdState"
import { Notification } from "src/queries/home/getNotifications"
import { markNotificationAsRead, getCanEditApplicantInfor } from "src/queries/home/markNotificationAsRead"
import { wait } from "src/utils/wait"
import { TeamNoticeType } from "src/aws/customAPI"
import { defaultTeam } from "src/constants/defaultTeam"
import { useIncrementRecoilState } from "src/recoil/hooks/useIncrementRecoilState"

dayjs.locale("ja")
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.updateLocale("ja", {
  relativeTime: {
    past: "今日",
    s: "今日",
    m: "今日",
    mm: "今日",
    h: "今日",
    hh: "今日",
    d: "1日前",
    dd: "%d日前",
    M: "1ヶ月前",
    MM: "%dヶ月前",
    y: "1年前",
    yy: "%d年前",
  },
})

export const NotificationTab = memo<RootTabScreenProps<typeof RootTabs.NotificationTab>>(({ navigation }) => {
  const { value: notifications, isLoading: isNotificationsLoading } = useAsyncSelector(notificationsSelector)
  const { value: organizations } = useAsyncSelector(homeOrganizationsSelector)
  const refreshNotifications = useIncrementRecoilState(notificationsRequestIdState)
  const refreshHomeOrganizations = useIncrementRecoilState(homeOrganizationsRequestIdState)
  const setOrganizationId = useSetRecoilState(selectedMyOrganizationState)

  const refresh = useCallback(() => {
    refreshNotifications()
    refreshHomeOrganizations()
  }, [refreshNotifications, refreshHomeOrganizations])

  const getOrganizationImageUrl = useCallback(
    (organizationId: string) => {
      const organization = organizations?.find((org) => org.id === organizationId)
      return organization?.uriImage
    },
    [organizations]
  )

  const renderNotificationItem = useCallback(
    ({ item }: ListRenderItemInfo<Notification>) => (
      <NotificationItem
        notification={item}
        setOrganizationId={setOrganizationId}
        uriImage={getOrganizationImageUrl(item.organizationId) || undefined}
        refreshNotifications={refreshNotifications}
      />
    ),
    [setOrganizationId, getOrganizationImageUrl, refreshNotifications]
  )

  const keyExtractor = useCallback((item: Notification) => item.id, [])

  const ListHeaderComponent = useMemo(
    () => (
      <View style={styles.headerContainer}>
        <Text style={styles.headerMessage}>「サービスからのお知らせ」を確認する場合は下記のボタンからご確認ください。</Text>
        <View style={styles.buttonContainer}>
          <IconButton
            buttonType={ButtonType.Primary}
            IconComponent={<></>}
            title="サービスからのお知らせ"
            onPress={() => navigation.navigate(Screens.Information)}
            style={styles.serviceButton}
          />
        </View>
      </View>
    ),
    [navigation]
  )

  const ListEmptyComponent = useMemo(
    () => (
      <View style={styles.emptyContainer}>
        <Text style={styles.noNotificationMessage}>現在、団体からのお知らせはありません。</Text>
      </View>
    ),
    []
  )

  return (
    <MainLayout>
      <FlatList
        data={notifications || []}
        renderItem={renderNotificationItem}
        keyExtractor={keyExtractor}
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        ListHeaderComponent={ListHeaderComponent}
        ListEmptyComponent={notifications && notifications.length === 0 ? ListEmptyComponent : null}
        refreshControl={<CustomRefreshControl refreshing={isNotificationsLoading} onRefresh={refresh} />}
      />
    </MainLayout>
  )
})

type NotificationItemProps = {
  notification: Notification
  setOrganizationId: (organizationId: string) => void
  uriImage?: string
  refreshNotifications: () => void
}

const NotificationItem = memo<NotificationItemProps>(({ notification, setOrganizationId, uriImage, refreshNotifications }) => {
  const navigation = useNavigation()
  const { value: tokens } = useAsyncSelector(tokensState)
  const accessToken = useMemo(() => tokens?.accessToken, [tokens])
  const setSelectedOrganization = useSetRecoilState(selectedMyOrganizationState)
  const [isMarkNotificationAsReadDone, setIsMarkNotificationAsReadDone] = useState(false)
  const setDefaultContactFilter = useSetRecoilState(defaultContactFilterState)

  useEffect(() => {
    if (accessToken && !notification.isRead && !isMarkNotificationAsReadDone) {
      setIsMarkNotificationAsReadDone(true)
    }
  }, [accessToken, notification.id, notification.isRead, isMarkNotificationAsReadDone, refreshNotifications])

  const getParamUrl = (url: string, param: string) => {
    const include = url.includes(param)
    if (!include) return ""
    const params = url.split(/([&,?,=])/)
    const index = params.indexOf(param)
    if (params.length < index + 3) return ""
    const value = params[index + 2]
    return value
  }

  const goToNext = useCallback(() => {
    switch (notification.type) {
      case "joinRequested":
        if (accessToken && !notification.isRead) {
          markNotificationAsRead({ accessToken, id: notification.id }).then(() => {
            wait(1000).then(() => refreshNotifications())
          })
        }
        navigation.navigate(Screens.PendingInvitations, { id: notification.organizationId })
        return
      case "joinRequestApproved":
        if (accessToken && !notification.isRead) {
          markNotificationAsRead({ accessToken, id: notification.id }).then(() => {
            wait(1000).then(() => refreshNotifications())
          })
        }
        setSelectedOrganization(notification.organizationId)
        navigation.navigate(Screens.Root, {
          screen: RootTabs.OrganizationManagerTab,
          params: {
            screen: OrganizationManagerScreens.OrganizationDetail,
            params: { organizationId: notification.organizationId },
          },
        })
        return
      case "remindToAnswer":
        if (accessToken && !notification.isRead) {
          markNotificationAsRead({ accessToken, id: notification.id }).then(() => {
            wait(1000).then(() => refreshNotifications())
          })
        }
        setOrganizationId(notification.organizationId)
        setDefaultContactFilter(["unanswered"])
        navigation.navigate(Screens.Root, {
          screen: RootTabs.ContactNetworkDrawer,
          params: {
            screen: ContactNetworkDrawers.InboxScreen,
            params: { organizationId: notification.organizationId },
          },
        })
        return
      case "matchingMessage":
        return
      case "hasMailSendFailedMember":
        return
      case "mailSendFailed":
        return
      case TeamNoticeType.startExtendOrderTime:
      case TeamNoticeType.middleExtendOrderTime:
      case TeamNoticeType.paymentDeadline:
        if (accessToken && !notification.isRead) {
          markNotificationAsRead({ accessToken, id: notification.id }).then(() => {
            wait(1000).then(() => refreshNotifications())
          })
        }
        setSelectedOrganization(notification.organizationId)
        if (notification.paidFunctionEnabled === false) {
          navigation.navigate(Screens.Root, {
            screen: RootTabs.OrganizationManagerTab,
            params: {
              screen: OrganizationManagerScreens.OrganizationDetail,
              params: { organizationId: notification.organizationId },
            },
          })
        } else {
          navigation.navigate(Screens.Root, {
            screen: RootTabs.OrganizationManagerTab,
            params: {
              screen: OrganizationManagerScreens.OrderDetail,
              params: {
                teamId: notification.organizationId,
                accountId: "",
                mode: "home",
              },
            },
          })
        }
        return
      case TeamNoticeType.transferApplicant: {
        setSelectedOrganization(notification.organizationId)
        if (accessToken && notification.organizationId) {
          getCanEditApplicantInfor({ accessToken, id: notification.organizationId }).then((canEditApplicantInfor) => {
            setSelectedOrganization(notification.organizationId)
            if (canEditApplicantInfor) {
              navigation.navigate(Screens.registerApplicantInformation, {
                organizationId: notification.organizationId,
                mode: "update",
                transferStatusId: getParamUrl(notification.url, "transferStatusId") ?? "",
                orderId: getParamUrl(notification.url, "orderId"),
              })
            } else {
              navigation.navigate(Screens.Root, {
                screen: RootTabs.OrganizationManagerTab,
                params: {
                  screen: OrganizationManagerScreens.OrganizationDetail,
                  params: { organizationId: notification.organizationId },
                },
              })
            }
          })
        }
        return
      }
      default:
        refreshNotifications()
    }
  }, [
    notification.type,
    notification.isRead,
    notification.organizationId,
    notification.id,
    notification.url,
    notification.paidFunctionEnabled,
    accessToken,
    navigation,
    setOrganizationId,
    setDefaultContactFilter,
    refreshNotifications,
    setSelectedOrganization,
  ])

  const checkDate = (date: Date) => {
    if (!date) return ""
    const weekday = ["日", "月", "火", "水", "木", "金", "土"]
    const nowDay = new Date()
    if (
      date.getFullYear() === nowDay.getFullYear() &&
      date.getMonth() === nowDay.getMonth() &&
      date.getDate() === nowDay.getDate()
    ) {
      return "今日"
    } else if (date.getFullYear() === nowDay.getFullYear()) {
      return `${dayjs(date).format("MM/DD")}(${weekday[date.getDay()]})`
    } else {
      return `${dayjs(date).format("YYYY/MM/DD")}(${weekday[date.getDay()]})`
    }
  }

  return (
    <Button style={styles.notificationItemContainer} key={notification.id} onPress={goToNext}>
      <Image style={styles.notificationItemImage} source={uriImage ? { uri: uriImage } : defaultTeam} resizeMode={"cover"} />
      <View style={styles.notificationItemInner}>
        <View style={styles.notificationItemTitleContainer}>
          {!notification.isRead ? (
            <View style={styles.notificationItemTitleIcon}>
              <BadgeIcon size={"small"} />
            </View>
          ) : null}
          <Text
            style={[
              styles.notificationItemTitle,
              notification.type == TeamNoticeType.transferApplicant ||
              notification.type == TeamNoticeType.paymentDeadline ||
              notification.type == TeamNoticeType.startExtendOrderTime ||
              notification.type == TeamNoticeType.middleExtendOrderTime
                ? { color: Colors.red }
                : {},
            ]}
          >
            {notification.title}
          </Text>
        </View>
        <Text style={styles.notificationItemText}>{notification.organizationName}</Text>
        <Text style={styles.notificationItemText}>
          {checkDate(notification.updatedAt)} {dayjs(notification.updatedAt).format("HH:mm")}
        </Text>
      </View>
    </Button>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  headerContainer: {
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 16,
    backgroundColor: Colors.white3,
  },
  headerMessage: {
    fontSize: 14,
    color: Colors.greyshBrown,
    textAlign: "left",
    lineHeight: 20,
    marginBottom: 16,
  },
  buttonContainer: {
    alignItems: "center",
    marginBottom: 16,
  },
  serviceButton: {
    width: "90%",
  },
  emptyContainer: {
    alignItems: "center",
    paddingTop: 40,
    paddingHorizontal: 20,
  },
  noNotificationMessage: {
    fontSize: 14,
    color: Colors.cement,
    textAlign: "center",
  },
  notificationItemContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: Colors.silver,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  notificationItemImage: {
    width: 64,
    height: 64,
    borderRadius: 4,
  },
  notificationItemInner: {
    marginHorizontal: 14,
    justifyContent: "space-between",
    alignItems: "flex-start",
    flex: 1,
  },
  notificationItemTitleContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  notificationItemTitleIcon: {
    marginRight: 5,
  },
  notificationItemTitle: {
    fontSize: 12,
    fontWeight: "bold",
    lineHeight: 17,
    color: Colors.greyshBrown,
    flex: 1,
  },
  notificationItemText: {
    fontSize: 11,
    lineHeight: 17,
    color: Colors.cement,
  },
})
